// Tenantra Inc. - Slide-in dispute summary opened from the Disputes table
import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { navigate } from '@/services/navigation';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { DetailSheet, Field, Section, StatusText, PrimaryAction, fmtDate } from './DetailPanel';

const STATUS = {
  open: { label: 'Awaiting your response', cls: 'text-amber-600', dot: 'bg-amber-500' },
  landlord_responded: { label: 'Response submitted', cls: 'text-blue-600', dot: 'bg-blue-500' },
  under_review: { label: 'Under review', cls: 'text-blue-600', dot: 'bg-blue-500' },
  resolved: { label: 'Resolved', cls: 'text-slate-500', dot: 'bg-slate-400' },
};
const REASONS = {
  not_my_tenancy: 'Not my tenancy', incorrect_dates: 'Incorrect dates', incorrect_amount: 'Incorrect amount',
  damage_not_caused: 'Damage not caused', payment_was_made: 'Payment was made', other: 'Other reason',
};
const fmtPeriod = (p) => {
  const [y, m] = (p || '').split('-');
  return y && m ? new Date(y, m - 1, 1).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }) : p;
};
const PAY = { on_time: 'On time', late: 'Late', partial: 'Partial', missed: 'Missed' };
const DECISIONS = { keep_entry: 'Entry kept as filed', remove_entry: 'Entry removed in the tenant’s favour', modify_entry: 'Entry corrected' };

export function DisputePanel({ disputeId, onClose }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!disputeId) return;
    setD(null);
    api.get(`/disputes/${disputeId}`).then(r => setD(r.data))
      .catch((err) => { toast.error(apiHelpers.handleError(err)); onClose(); });
  }, [disputeId]);

  const canRespond = d?.status === 'open';

  return (
    <DetailSheet
      open={!!disputeId} onClose={onClose} loading={!d} testId="dispute-panel"
      crumb="Disputes / Dispute" title={d?.tenant_name}
      meta={d && <><span className="text-slate-500">Opened {fmtDate(d.created_at)}</span><StatusText cfg={STATUS[d.status] || STATUS.open} /></>}
      footer={d && <PrimaryAction onClick={() => navigate(`/landlord/disputes/${d.id}`)} testId="panel-open-dispute">{canRespond ? 'Respond to dispute' : 'Open dispute'} <ArrowRight className="w-4 h-4" /></PrimaryAction>}
    >
      {d && <>
        <Section title="Disputed entry">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Property" wide>{d.property_address}</Field>
            <Field label="Month">{fmtPeriod(d.report_period)}</Field>
            <Field label="Reported as">{PAY[d.report_payment_status] || d.report_payment_status}</Field>
          </dl>
        </Section>
        <Section title="Tenant’s claim">
          <p className="text-sm font-medium text-slate-900 mb-1">{REASONS[d.reason] || d.reason}</p>
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="panel-dispute-explanation">{d.explanation}</p>
          {d.tenant_evidence_file_ids?.length > 0 && <p className="text-xs text-slate-400 mt-2">{d.tenant_evidence_file_ids.length} evidence file(s) attached</p>}
        </Section>
        <Section title="Your response">
          {d.landlord_response
            ? <p className="text-sm text-slate-600 leading-relaxed">{d.landlord_response}</p>
            : <p className="text-sm text-slate-500">{canRespond ? 'You haven’t responded yet.' : 'No response was submitted.'}</p>}
        </Section>
        <Section title="Outcome">
          {d.status === 'resolved'
            ? <p className="text-sm text-slate-800">{DECISIONS[d.admin_decision] || 'Resolved'}{d.resolved_at ? ` on ${fmtDate(d.resolved_at)}` : ''}</p>
            : <p className="text-sm text-slate-500">Pending. The entry is flagged as disputed until a decision is made.</p>}
        </Section>
      </>}
    </DetailSheet>
  );
}
